'use client';

import { useAuth } from '@clerk/nextjs';
import { type UpdateNeededReason, Zero } from '@rocicorp/zero';
import { ZeroProvider } from '@rocicorp/zero/react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { schema } from '../zero-schema';
import { clientMutators } from '../zero/client-mutators';

interface ZeroClientProviderProps {
  children: ReactNode;
  /**
   * The signed-in user's id, already resolved server-side (e.g. via
   * `auth()` in a Server Component). Clerk's client SDK needs a real
   * network round trip (`/v1/client` + `/v1/environment`, several hundred
   * ms against a dev instance) before `useAuth()`'s `userId` resolves on
   * the client -- using this instead lets Zero (and therefore the whole
   * app shell) initialize on the first render rather than waiting on that.
   */
  userId: string;
}

function buildZeroClient(
  userId: string,
  token: string | undefined,
  onUpdateNeeded: (reason: UpdateNeededReason) => void
) {
  return new Zero({
    schema,
    userID: userId,
    server: process.env.NEXT_PUBLIC_ZERO_SERVER_URL,
    auth: token,
    mutators: clientMutators,
    // Passed to custom queries (see zero/queries.ts) for client-side
    // optimistic/local evaluation -- must match the QueryCtx shape the
    // /api/zero/query route resolves server-side from the verified session.
    context: { sub: userId },
    onUpdateNeeded
  });
}
type ZeroClient = ReturnType<typeof buildZeroClient>;

// Clerk's default session token expires after 60s. Zero only re-fetches a
// token reactively, when a push/query is rejected with 401 and the
// connection drops into "needs-auth" -- it doesn't refresh proactively. Left
// alone, the first mutation or query after ~60s of idle time fails and the
// user sees a "Could not save" toast before Zero recovers. Refresh well
// before expiry so pushes never hit a stale token in the first place.
const AUTH_REFRESH_INTERVAL_MS = 30_000;
const AUTH_ERROR = 'Session expired. Retry the connection or sign in again.';

export function ZeroClientProvider({
  children,
  userId
}: ZeroClientProviderProps) {
  // getToken is available on the pre-load branch of useAuth()'s return type
  // too -- Clerk queues the call internally until its client SDK is ready --
  // so it's safe to invoke before `isLoaded` without waiting on it here.
  const { getToken } = useAuth();
  const [zero, setZero] = useState<ZeroClient | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const retryAuth = useRef<() => void>(() => {});

  useEffect(() => {
    let cancelled = false;
    let zeroInstance: ZeroClient | null = null;
    let unsubscribe: (() => void) | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let refreshTimer: ReturnType<typeof setInterval> | undefined;
    let schemaMismatch = false;
    let authInFlight: Promise<void> | undefined;

    function handleUpdateNeeded(updateReason: UpdateNeededReason) {
      if (
        updateReason.type !== 'SchemaVersionNotSupported' ||
        cancelled ||
        schemaMismatch
      ) {
        return;
      }

      schemaMismatch = true;
      console.error('Zero schema version is not supported', updateReason);
      unsubscribe?.();
      unsubscribe = undefined;
      if (refreshTimer) clearInterval(refreshTimer);
      refreshTimer = undefined;
      const incompatibleZero = zeroInstance;
      zeroInstance = null;
      incompatibleZero?.close();
      setZero(current => (current === incompatibleZero ? null : current));
      setError(
        'Live data is unavailable because this app version does not match the sync server. Please try again after the deployment finishes.'
      );
    }

    function refreshAuth() {
      if (cancelled || schemaMismatch || !zeroInstance || authInFlight) return;
      const client = zeroInstance;
      const current = () =>
        !cancelled && !schemaMismatch && zeroInstance === client;
      // Always bypass Clerk's cache: a needs-auth event must not reuse the
      // rejected token, including when it overlaps a periodic refresh.
      authInFlight = Promise.resolve()
        .then(() => getToken({ skipCache: true }))
        .then(async token => {
          if (!current()) return;
          if (!token) throw new Error('Missing session token');
          await client.connection.connect({ auth: token });
          if (current() && client.connection.state.current.name === 'connected')
            setError(null);
        })
        .catch(() => {
          if (current()) setError(AUTH_ERROR);
        })
        .finally(() => {
          authInFlight = undefined;
        });
    }
    retryAuth.current = refreshAuth;

    try {
      // Build and hand out the Zero client immediately, before the auth
      // token has resolved. Waiting on `getToken()` (a network round trip)
      // before ever calling `setZero` used to block the entire app shell --
      // including server-rendered content that doesn't touch Zero at all --
      // behind a full-page "Connecting..." placeholder. Zero supports
      // starting unauthenticated and reconnecting once a token is available
      // (same mechanism `refreshAuth` already uses below), so authenticate
      // in the background instead.
      zeroInstance = buildZeroClient(userId, undefined, handleUpdateNeeded);

      // Defensive guard in case a Zero implementation reports the update
      // synchronously while its constructor is still running.
      if (schemaMismatch) {
        zeroInstance.close();
        zeroInstance = null;
        return;
      }

      unsubscribe = zeroInstance.connection.state.subscribe(state => {
        if (cancelled || schemaMismatch) return;
        if (state.name === 'connected') setError(null);
        if (state.name === 'needs-auth') {
          setError(AUTH_ERROR);
          refreshAuth();
        }
      });
      refreshTimer = setInterval(refreshAuth, AUTH_REFRESH_INTERVAL_MS);

      setError(null);
      setZero(zeroInstance);

      refreshAuth();
    } catch (err) {
      if (schemaMismatch) return;
      console.error('Failed to initialize Zero client', err);
      if (cancelled) return;
      setError('Could not connect to live data.');
      setZero(null);
      const delay = Math.min(1000 * 2 ** retryCount, 15000);
      retryTimer = setTimeout(() => {
        setRetryCount(count => count + 1);
      }, delay);
    }

    return () => {
      cancelled = true;
      retryAuth.current = () => {};
      unsubscribe?.();
      if (retryTimer) clearTimeout(retryTimer);
      if (refreshTimer) clearInterval(refreshTimer);
      setZero(prev => (prev === zeroInstance ? null : prev));
      zeroInstance?.close();
    };
  }, [userId, getToken, retryCount]);

  // Children call useZero()/useQuery(). Never render them without
  // ZeroProvider -- or those hooks throw "useZero must be used within a
  // ZeroProvider". Callers only mount this component once a signed-in
  // `userId` is known server-side (see AppLayout), so `zero` readiness is
  // the only thing gating render here.
  if (!zero) {
    if (error) {
      return (
        <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 p-6 text-body text-secondary-foreground">
          <p>{error}</p>
          <button
            type="button"
            className="min-h-11 text-label underline hover:text-foreground"
            onClick={() => {
              setError(null);
              setRetryCount(count => count + 1);
            }}
          >
            Retry
          </button>
        </div>
      );
    }

    return (
      <div className="flex min-h-[40vh] items-center justify-center p-6 text-body text-muted-foreground">
        Connecting…
      </div>
    );
  }

  return (
    <ZeroProvider zero={zero}>
      {error ? (
        <div
          role="alert"
          className="fixed inset-x-3 top-3 z-50 mx-auto flex max-w-xl items-center gap-3 rounded-md border bg-background p-3 text-body shadow-md"
        >
          <span className="flex-1">{error}</span>
          <button
            type="button"
            className="min-h-11 underline"
            onClick={() => retryAuth.current()}
          >
            Retry
          </button>
        </div>
      ) : null}
      {children}
    </ZeroProvider>
  );
}
